import {FixedSizeList as Lists} from 'react-window';
import {
  Box,
  CircularProgress,
  FormControlLabel,
  InputAdornment,
  ListItemButton,
  ListItemText,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {WarningAmber, DoNotDisturb} from '@mui/icons-material';
import {makeStyles} from '@mui/styles';
import {useCallback, useEffect, useState, useMemo} from 'react';
import {AxiosResponse} from 'axios';
import {
  IParameterDetailsProp,
  CustomDivider,
  IPartnerProps,
} from '../TnnCampaignCreationStepper';
import MusicPreviewBtn from '../../../../components/music/MusicPreviewBtn';
import {IOSSwitch} from '../../../../components/styles/ToggleButton';
import {ICompany} from '../../../../dto/Company.dto';
import useDebounce from '../../../../components/utlis/useDebounce';
import {CustomTextarea} from '../../../../components/styles/CustomTextarea';
import {apiPut} from '../../../../axiosConfig';
import {IApiSuccessMsgResponse} from '../../../../dto/API.dto';
import {TNN_PERCENTAGE_DEFAULT} from '../../../../config/messageConfig';

export const ReviewCmpt = ({
  campaignName,
  adSource,
  campaignType,
  commercialLength,
  type,
  parameter,
  partners,
  isReview = true,
  notes,
  sponsorRequestId,
  scheduleId,
  onCompanyPriceChange,
  tnnPercentageValue = TNN_PERCENTAGE_DEFAULT,
}: {
  campaignName: string;
  adSource: string;
  type: string;
  commercialLength: string;
  campaignType: string[];
  parameter: IParameterDetailsProp;
  partners: IPartnerProps;
  isReview?: boolean;
  notes?: string;
  sponsorRequestId?: number;
  scheduleId?: number;
  onCompanyPriceChange?: (
    type: 'gametime' | 'neptuneRadio',
    companyId: number,
    sharePrice: number,
    dollarValue: number,
  ) => void;
  tnnPercentageValue?: number;
}) => {
  const handleGameTimePriceChange = useCallback(
    (companyId: number, sharePrice: number, dollarValue: number) =>
      onCompanyPriceChange?.('gametime', companyId, sharePrice, dollarValue),
    [onCompanyPriceChange],
  );

  const handleNeptuneRadioPriceChange = useCallback(
    (companyId: number, sharePrice: number, dollarValue: number) =>
      onCompanyPriceChange?.(
        'neptuneRadio',
        companyId,
        sharePrice,
        dollarValue,
      ),
    [onCompanyPriceChange],
  );

  return (
    <Box>
      {isReview && (
        <Typography className='font-bold text-16 mb-2'>Review</Typography>
      )}

      <Stack direction={'row'} className='my-2'>
        <Typography className='text-white-50 w-200'>
          Campaign Name/Type
        </Typography>
        <Typography>{`${campaignName} / ${campaignType.join(',')}`}</Typography>
      </Stack>
      {type === 'H' && (
        <>
          <Stack direction={'row'} className='my-2'>
            <Typography className='text-white-50 w-300'>AD Source</Typography>
            <Typography>{adSource}</Typography>
          </Stack>
          <Stack direction={'row'} className='my-2'>
            <Typography className='text-white-50 w-300'>
              Commercial Length
            </Typography>
            <Typography>{commercialLength}</Typography>
          </Stack>
        </>
      )}
      <CustomDivider />
      <Box className='my-2'>
        <Typography className='text-white-50 w-200 my-2'>
          {`Campaign Parameter`}
        </Typography>
        <Stack direction={'row'} className='my-2'>
          <Typography className='text-white-50 w-200'>Date</Typography>
          {parameter.start_date && parameter.end_date ? (
            <Typography>{`${parameter.start_date.format(
              'MM-DD-YYYY',
            )} / ${parameter.end_date.format('MM-DD-YYYY')}`}</Typography>
          ) : null}
        </Stack>
        <Stack direction={'row'} className='my-2'>
          <Typography className='text-white-50 w-200'>
            Number Of Runs
          </Typography>
          {parameter.number_of_runs}
        </Stack>
        <Table aria-label='simple table'>
          <TableHead>
            <TableRow>
              <TableCell className='w-160'>Executions/Spots</TableCell>
              <TableCell className='w-160'>Label</TableCell>
              <TableCell className='w-120'>weight</TableCell>
              <TableCell className='w-110'>Primary</TableCell>
              <TableCell>TNN Ad File</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {parameter.slots &&
              parameter.slot_details.map(val => (
                <TableRow key={val.tnn_campaign_slot}>
                  <TableCell align='center'>{val.tnn_campaign_slot}</TableCell>
                  <TableCell>{val?.label ?? ''}</TableCell>
                  <TableCell>{val.tnn_campaign_weightage}</TableCell>
                  <TableCell>
                    <FormControlLabel
                      className='ml-2'
                      label=''
                      control={
                        <IOSSwitch
                          defaultChecked
                          inputProps={{'aria-label': 'Primary Switch'}}
                        />
                      }
                      checked={Boolean(val.tnn_campaign_is_primary)}
                    />
                  </TableCell>
                  <TableCell>
                    {val.file ? (
                      <Stack direction='row' alignItems='center'>
                        <Typography className='mr-2'>
                          {val.file.name}
                        </Typography>
                        <MusicPreviewBtn
                          id={val.tnn_campaign_slot}
                          url={URL.createObjectURL(val.file)}
                          track_name={val.file.name}
                          track_artist={val.file.name}
                        />
                      </Stack>
                    ) : (
                      <Stack direction='row' alignItems={'center'}>
                        <Typography className='mr-2'>
                          {val.media_track?.file_name ?? val.fileName ?? ''}
                        </Typography>
                        <MusicPreviewBtn
                          id={val.tnn_campaign_slot}
                          url={
                            val.media_track
                              ? val.media_track.url
                              : val.initial_url ?? ''
                          }
                          track_name={
                            val.media_track
                              ? val.media_track.track_name
                              : val.fileName ?? ''
                          }
                          track_artist={
                            val.media_track
                              ? val.media_track.track_artist
                              : val.fileName ?? ''
                          }
                        />
                      </Stack>
                    )}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </Box>
      <Box>
        <Stack direction={'row'} spacing={2} className='overflow-x'>
          {campaignType.includes('GameTime') && (
            <CompanyListCmpt
              title='GameTime Partners'
              companies={partners.gametime.selected_companies}
              onPriceChange={handleGameTimePriceChange}
              tnnPercentageValue={tnnPercentageValue}
              isView={!isReview}
            />
          )}
          {campaignType.includes('Neptune Radio') && (
            <CompanyListCmpt
              title='Neptune Radio Partners'
              companies={partners.neptuneRadio.selected_companies}
              onPriceChange={handleNeptuneRadioPriceChange}
              tnnPercentageValue={tnnPercentageValue}
              isView={!isReview}
            />
          )}
          {notes !== undefined && sponsorRequestId && scheduleId ? (
            <DebouncedTextField
              notes={notes}
              url={`v1/tnn/sponsor/${sponsorRequestId}/campaign/${scheduleId}/notes`}
            />
          ) : null}
        </Stack>
      </Box>
    </Box>
  );
};

const useListStyles = makeStyles({
  listContainer: {
    width: '300px',
    height: '275px',
    overflow: 'auto',
    backgroundColor: '#1a1a1a',
    padding: '16px',
    borderRadius: '8px',
    color: 'white',
  },
  stickyHeader: {
    position: 'sticky',
    top: 0,
    backgroundColor: '#000000',
    zIndex: 1,
    padding: '8px 15px',
  },
  listItemButton: {
    padding: '0',
    backgroundColor: '#3B7ABC !important',
    marginBottom: '8px',
    '&:hover': {
      backgroundColor: '#1E5562 !important',
      border: 'none !important',
    },
    border: 'none !important',
    borderRadius: '3px !important',
    height: '40px',
    margin: '10px 0px !important',
  },
  activeListItemButton: {
    backgroundColor: '#1E5562 !important',
  },
  listItemText: {
    color: 'white',
    textAlign: 'center',
  },
  scrollbar: {
    '&::-webkit-scrollbar': {
      width: '6px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#007BFF',
    },
  },
});
interface ICompanyWithPricing extends ICompany {
  share_price?: number;
  dollar_value?: number;
  status?: string;
}

type IRowData = {
  companies: ICompany[];
  onPriceChange: (
    companyId: number,
    sharePrice: number,
    dollarValue: number,
  ) => void;
  classes: Record<string, string>;
  tnnPercentageValue: number;
  isView: boolean;
};

const Row = ({
  index,
  style,
  data,
}: {
  index: number;
  style: React.CSSProperties;
  data: IRowData;
}) => {
  const {companies, onPriceChange, classes, tnnPercentageValue, isView} = data;
  const value = companies[index] as ICompanyWithPricing;
  const [sharePrice, setSharePrice] = useState<number>(value.share_price || 0);
  const [inputValue, setInputValue] = useState(String(value.share_price || 0));
  const dollarValue = sharePrice * (tnnPercentageValue / 100);

  useEffect(() => {
    if (value.share_price !== undefined) {
      setSharePrice(value.share_price);
      setInputValue(String(value.share_price));
    }
  }, [value.share_price]);

  useEffect(() => {
    const newDollarValue = sharePrice * (tnnPercentageValue / 100);
    if (onPriceChange && sharePrice !== (value.share_price || 0)) {
      onPriceChange(value.id, sharePrice, newDollarValue);
    }
  }, [
    sharePrice,
    value.id,
    tnnPercentageValue,
    onPriceChange,
    value.share_price,
  ]);

  const isSuspended =
    Boolean(value.suspended_at);
  const isDeactivated =
    Boolean(value.deleted_at);
  const statusMessage = isDeactivated
    ? 'Partner is Deactivated'
    : isSuspended
    ? 'Partner is Suspended'
    : '';

  const handleSharePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (/^\d{0,6}(\.\d{0,2})?$/.test(val)) {
      setInputValue(val);
    }
  };

  const handleSharePriceBlur = () => {
    const parsedValue = parseFloat(inputValue);
    const newValue = isNaN(parsedValue) || parsedValue < 0 ? 0 : parsedValue;
    const roundedValue = Math.round(newValue * 100) / 100;
    setSharePrice(roundedValue);
    setInputValue(String(roundedValue));
  };

  return (
    <Box
      style={{...style, padding: '0px 6px 6px 6px'}}
      className='flex gap-2 items-center'>
      <ListItemButton
        key={value.id}
        divider
        className={`${classes.listItemButton} w-300 min-w-300 h-40`}
        dense>
        <Box
          component='div'
          className='flex-grow flex gap-1 items-center py-0 px-16px'>
          <ListItemText
            primary={value.name}
            classes={{primary: classes.listItemText}}
          />
        </Box>
        {isDeactivated ? (
          <Tooltip title={statusMessage} arrow placement='top'>
            <DoNotDisturb
              className='text-18 no-shrink'
              color='error'
            />
          </Tooltip>
        ) : isSuspended ? (
          <Tooltip title={statusMessage} arrow placement='top'>
            <WarningAmber
              className='text-18 no-shrink'
              color='warning'
            />
          </Tooltip>
        ) : null}
      </ListItemButton>

      <Box className='flex flex-row gap-1 items-center'>
        <TextField
          size='small'
          type='number'
          value={inputValue}
          onChange={handleSharePriceChange}
          onKeyDown={e =>
            ['e', 'E', '+', '-'].includes(e.key) && e.preventDefault()
          }
          onBlur={handleSharePriceBlur}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start' className='mr-4px'>
                $
              </InputAdornment>
            ),
          }}
          inputProps={{'aria-label': `Share price for ${value.name}`}}
          disabled={isView}
          fullWidth
        />

        <TextField
          size='small'
          type='number'
          value={dollarValue.toFixed(2)}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start' className='mr-4px'>
                $
              </InputAdornment>
            ),
            readOnly: true,
          }}
          inputProps={{'aria-label': `Dollar value for ${value.name}`}}
          disabled
          fullWidth
        />
      </Box>
    </Box>
  );
};

export const CompanyListCmpt = ({
  title,
  companies,
  onPriceChange,
  tnnPercentageValue = TNN_PERCENTAGE_DEFAULT,
  isView = false,
}: {
  title: string;
  companies: ICompany[];
  onPriceChange?: (
    companyId: number,
    sharePrice: number,
    dollarValue: number,
  ) => void;
  tnnPercentageValue?: number;
  isView?: boolean;
}) => {
  const classes = useListStyles();

  // Track current prices for dynamic total calculation
  const [currentPrices, setCurrentPrices] = useState<
    Record<number, {sharePrice: number; dollarValue: number}>
  >({});

  // Initialize currentPrices from companies prop
  useEffect(() => {
    const initialPrices: Record<
      number,
      {sharePrice: number; dollarValue: number}
    > = {};
    companies.forEach(company => {
      const companyWithPrice = company as ICompanyWithPricing;
      initialPrices[company.id] = {
        sharePrice: companyWithPrice.share_price || 0,
        dollarValue: companyWithPrice.dollar_value || 0,
      };
    });
    setCurrentPrices(initialPrices);
  }, [companies]);

  const handlePriceChange = useCallback(
    (companyId: number, sharePrice: number, dollarValue: number) => {
      setCurrentPrices(prev => ({
        ...prev,
        [companyId]: {sharePrice, dollarValue},
      }));
      onPriceChange?.(companyId, sharePrice, dollarValue);
    },
    [onPriceChange],
  );

  const itemData = useMemo(
    () => ({
      companies,
      onPriceChange: handlePriceChange,
      classes,
      tnnPercentageValue,
      isView,
    }),
    [companies, handlePriceChange, classes, tnnPercentageValue, isView],
  );

  // Calculate totals from current prices
  const totalSharePrice = useMemo(() => {
    return Object.values(currentPrices).reduce((sum, price) => {
      return sum + (price.sharePrice || 0);
    }, 0);
  }, [currentPrices]);

  const totalDollarValue = useMemo(() => {
    return Object.values(currentPrices).reduce((sum, price) => {
      return sum + (price.dollarValue || 0);
    }, 0);
  }, [currentPrices]);

  return (
    <Stack spacing={1} className='w-full'>
      <Stack direction='row' alignItems='flex-end' className='p-8px px-15px'>
        <Typography variant='h6' className='w-200 min-w-200'>
          {`${title} ( ${companies ? companies.length : 0} )`}
        </Typography>
        <Stack direction='row' className='flex-grow' alignItems='flex-end'>
          <Typography
            variant='subtitle2'
            className='flex-grow text-center'
            noWrap>
            Share Price
          </Typography>
          <Stack className='flex-grow' alignItems='center'>
            <Typography
              variant='caption'
              className='text-white-50 ml-16'
              align='center'
              noWrap>
              Auto-calculated ({tnnPercentageValue}%)
            </Typography>
            <Typography
              variant='subtitle2'
              className='ml-5'
              align='center'
              noWrap>
              Dollar Value
            </Typography>
          </Stack>
        </Stack>
      </Stack>

      <Lists
        className={`${classes.listContainer} ${classes.scrollbar} overflow-auto scrollbar bg-dark border-radius-0`}
        height={176}
        itemCount={companies.length}
        itemSize={44}
        width='100%'
        itemData={itemData}>
        {Row}
      </Lists>

      <Stack direction='row' alignItems='center' className='p-8px px-15px'>
        <Typography variant='h6' className='w-200 min-w-200 font-bold'>
          Totals
        </Typography>
        <Stack direction='row' className='flex-grow'>
          <Typography
            variant='subtitle1'
            className='flex-grow text-center font-bold'>
            ${totalSharePrice.toFixed(2)}
          </Typography>
          <Typography
            variant='subtitle1'
            className='flex-grow text-center font-bold'>
            ${totalDollarValue.toFixed(2)}
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  );
};

export const DebouncedTextField = ({
  notes,
  url,
}: {
  notes: string;
  url: string;
}) => {
  const [newNotes, setNewNotes] = useState(notes);
  const [isLoading, setIsLoading] = useState(false);

  const debouncedText = useDebounce(newNotes, 1000);

  const handleSubmit = useCallback(() => {
    apiPut(url, {
      notes: debouncedText,
    })
      .then((_response: AxiosResponse<IApiSuccessMsgResponse>) => {
        setIsLoading(false);
      })
      .catch(_error => {
        setIsLoading(false);
      });
  }, [url, debouncedText]);

  useEffect(() => {
    if (debouncedText === notes) return;
    const runApi = async () => {
      setIsLoading(true);
      await handleSubmit();
    };

    runApi();
  }, [debouncedText, handleSubmit, notes]);

  const handleChange = useCallback(e => {
    setNewNotes(e.target.value);
  }, []);

  return (
    <Box position='relative' minWidth='360px' height='375px'>
      <CustomTextarea
        id='message-description'
        placeholder='Enter Note here'
        onChange={handleChange}
        value={newNotes}
        sx={{
          height: '100%',
          m: 0,
          p: 0,
          '& .MuiInputBase-root': {
            height: '100%',
            alignItems: 'stretch',
            backgroundColor: 'transparent',
          },
        }}
      />
      {isLoading && (
        <Box className='absolute flex items-center justify-center'>
          <CircularProgress size={20} />
        </Box>
      )}
    </Box>
  );
};
